import React, { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components'
import apiClient, { AgentMemoryItem, ExpertCatalogItem, LearnedShorthandEntry, PromptExampleBucket, PromptTextExample } from '@/lib/apiClient'

type Bucket = PromptExampleBucket

const BUCKET_OPTIONS: Array<{ value: Bucket; label: string; help: string }> = [
  {
    value: 'standard',
    label: 'Catalant Standard',
    help: 'Applies to the Catalant standard final pitch agent.',
  },
  {
    value: 'team',
    label: 'Catalant Team',
    help: 'Applies to the Catalant team-voice final pitch agent.',
  },
  {
    value: 'idb',
    label: 'Catalant IDB',
    help: 'Applies to the Catalant IDB final pitch agent.',
  },
  {
    value: 'idb_team',
    label: 'Catalant IDB Team',
    help: 'Applies to the Catalant IDB team-voice final pitch agent.',
  },
  {
    value: 'umbrex',
    label: 'Umbrex Proposal',
    help: 'Applies to the Umbrex final proposal agent. Paste previously submitted Umbrex proposals here.',
  },
  {
    value: 'umbrex_team',
    label: 'Umbrex Team Proposal',
    help: 'Applies to the Umbrex blended-team proposal agent.',
  },
]

// ── Toggle Switch ────────────────────────────────────────────────────────────
function Toggle({
  checked,
  onChange,
  disabled,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-blue-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

// ── Section card ─────────────────────────────────────────────────────────────
function SectionCard({
  title,
  subtitle,
  icon,
  children,
  className = '',
  testId,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div className={`bp-card overflow-hidden ${className}`} data-testid={testId}>
      <div className="flex items-start gap-4 border-b border-slate-100 px-6 py-5">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100">
          {icon}
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

// ── Drop zone ────────────────────────────────────────────────────────────────
function DropZone({ onFile, disabled }: { onFile: (f: File) => void; disabled?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) onFile(file)
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-all duration-150 ${
        dragging
          ? 'border-blue-400 bg-blue-50'
          : disabled
            ? 'cursor-not-allowed border-slate-200 bg-slate-50'
            : 'border-slate-300 bg-white hover:border-blue-300 hover:bg-blue-50/40'
      }`}
    >
      <svg className={`mb-3 h-8 w-8 ${dragging ? 'text-blue-500' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
      <p className="text-sm font-medium text-slate-700">
        {dragging ? 'Drop the PDF here' : 'Drag & drop a PDF, or click to browse'}
      </p>
      <p className="mt-1 text-xs text-slate-400">PDF files only</p>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onFile(f)
          e.target.value = ''
        }}
      />
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export const KnowledgeAssetsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState('')
  const [bucket, setBucket] = useState<Bucket>('standard')
  const [examplesDraft, setExamplesDraft] = useState<PromptTextExample[]>([])
  const [examplesError, setExamplesError] = useState('')
  const [examplesSavedMessage, setExamplesSavedMessage] = useState('')
  const [memorySearch, setMemorySearch] = useState('')
  const [memoryDeleteError, setMemoryDeleteError] = useState('')

  const assetsQuery = useQuery({
    queryKey: ['workspace-assets'],
    queryFn: apiClient.getWorkspaceAssets,
  })

  const promptExamplesQuery = useQuery({
    queryKey: ['workspace-prompt-examples', bucket],
    queryFn: () => apiClient.getPromptExamples(bucket),
  })

  const agentMemoriesQuery = useQuery({
    queryKey: ['workspace-agent-memories'],
    queryFn: () => apiClient.getAgentMemories(),
  })

  React.useEffect(() => {
    const data = promptExamplesQuery.data?.examples || []
    setExamplesDraft(data)
    setExamplesError('')
    setExamplesSavedMessage('')
  }, [promptExamplesQuery.data, bucket])

  const uploadMutation = useMutation({
    mutationFn: (args: { file: File; replace: boolean }) =>
      apiClient.uploadCatalantProjectPdf(args.file, args.replace),
    onSuccess: () => {
      setSelectedPdfFile(null)
      setUploadError('')
      queryClient.invalidateQueries({ queryKey: ['workspace-assets'] })
    },
    onError: (err: any) => {
      setUploadError(String(err?.response?.data?.detail || err?.message || 'Failed to upload PDF'))
    },
  })

  const expertCatalogQuery = useQuery({
    queryKey: ['workspace-experts'],
    queryFn: apiClient.getExpertCatalog,
  })

  const updateAssetsMutation = useMutation({
    mutationFn: (payload: {
      catalant_project_tool_mode_enabled?: boolean
      umbrex_dc_bio?: string
      umbrex_dc_expert_id?: string
    }) =>
      apiClient.updateWorkspaceAssets(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-assets'] })
    },
    onError: (err: any) => {
      setUploadError(String(err?.response?.data?.detail || err?.message || 'Failed to update project source mode'))
    },
  })

  const [dcBioDraft, setDcBioDraft] = useState<string | null>(null)
  const [dcBioError, setDcBioError] = useState('')
  const [dcBioSaved, setDcBioSaved] = useState(false)
  const saveDcExpertMutation = useMutation({
    mutationFn: (value: string) => apiClient.updateWorkspaceAssets({ umbrex_dc_expert_id: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-assets'] })
      queryClient.invalidateQueries({ queryKey: ['workspace-experts'] })
    },
    onError: (err: any) => {
      setDcBioError(String(err?.response?.data?.detail || err?.message || 'Failed to save DC expert'))
    },
  })
  const saveDcBioMutation = useMutation({
    mutationFn: (value: string) => apiClient.updateWorkspaceAssets({ umbrex_dc_bio: value }),
    onSuccess: () => {
      setDcBioDraft(null)
      setDcBioSaved(true)
      queryClient.invalidateQueries({ queryKey: ['workspace-assets'] })
    },
    onError: (err: any) => {
      setDcBioError(String(err?.response?.data?.detail || err?.message || 'Failed to save DC bio'))
    },
  })

  const shorthandQuery = useQuery({
    queryKey: ['workspace-umbrex-shorthand'],
    queryFn: apiClient.getLearnedShorthand,
  })
  const [shorthandError, setShorthandError] = useState('')
  const deleteShorthandMutation = useMutation({
    mutationFn: (entry: { code: string; family_key: string }) => apiClient.deleteLearnedShorthand(entry),
    onSuccess: () => {
      setShorthandError('')
      queryClient.invalidateQueries({ queryKey: ['workspace-umbrex-shorthand'] })
    },
    onError: (err: any) => {
      setShorthandError(String(err?.response?.data?.detail || err?.message || 'Failed to forget entry'))
    },
  })

  const saveExamplesMutation = useMutation({
    mutationFn: async () => {
      const normalized = examplesDraft.map((row, idx) => ({
        id: row.id,
        title: String(row.title || '').trim(),
        text: String(row.text || '').trim(),
        enabled: Boolean(row.enabled),
        order: idx + 1,
      }))
      return apiClient.replacePromptExamples(bucket, normalized)
    },
    onSuccess: async () => {
      setExamplesError('')
      setExamplesSavedMessage('Examples saved successfully.')
      await queryClient.invalidateQueries({ queryKey: ['workspace-prompt-examples', bucket] })
    },
    onError: (err: any) => {
      setExamplesSavedMessage('')
      setExamplesError(String(err?.response?.data?.detail || err?.message || 'Failed to save examples'))
    },
  })

  const reseedMutation = useMutation({
    mutationFn: () => apiClient.reseedPromptExamples(bucket),
    onSuccess: async () => {
      setExamplesError('')
      setExamplesSavedMessage('Default examples restored.')
      await queryClient.invalidateQueries({ queryKey: ['workspace-prompt-examples', bucket] })
    },
    onError: (err: any) => {
      setExamplesSavedMessage('')
      setExamplesError(String(err?.response?.data?.detail || err?.message || 'Failed to reseed defaults'))
    },
  })

  const deleteMemoryMutation = useMutation({
    mutationFn: (memoryId: number) => apiClient.deleteAgentMemory(memoryId),
    onSuccess: async () => {
      setMemoryDeleteError('')
      await queryClient.invalidateQueries({ queryKey: ['workspace-agent-memories'] })
    },
    onError: (err: any) => {
      setMemoryDeleteError(String(err?.response?.data?.detail || err?.message || 'Failed to delete memory'))
    },
  })

  const currentPath = String(assetsQuery.data?.assets?.catalant_project_experience_pdf_path || '').trim()
  const projectToolModeEnabled = assetsQuery.data?.assets?.catalant_project_tool_mode_enabled !== false
  const savedDcBio = String(assetsQuery.data?.assets?.umbrex_dc_bio || '')
  const dcBioValue = dcBioDraft ?? savedDcBio
  const dcBioDirty = dcBioDraft !== null && dcBioDraft !== savedDcBio
  const shorthandEntries: LearnedShorthandEntry[] = shorthandQuery.data?.entries || []
  const savedDcExpertId = String(assetsQuery.data?.assets?.umbrex_dc_expert_id || '')
  const experts: ExpertCatalogItem[] = expertCatalogQuery.data?.experts || []
  const catalogOk = expertCatalogQuery.data?.status === 'success'
  const dcExpert = expertCatalogQuery.data?.dc_expert || null
  const catalogBio = String(dcExpert?.summary || '').trim()
  const effectiveBioSource = catalogBio ? 'expert catalog' : savedDcBio.trim() ? 'text below' : 'nothing configured' 
  const currentBucketMeta = useMemo(
    () => BUCKET_OPTIONS.find((item) => item.value === bucket) || BUCKET_OPTIONS[0],
    [bucket]
  )
  const memories = agentMemoriesQuery.data?.memories || []
  const filteredMemories = useMemo(() => {
    const query = memorySearch.trim().toLowerCase()
    if (!query) return memories
    return memories.filter((memory) => {
      const content = [
        memory.agent_slug,
        memory.platform_type,
        memory.memory_type,
        memory.memory_text,
        String(memory.content?.applies_when || ''),
        String(memory.content?.preference || ''),
        String(memory.content?.agent_behavior || ''),
      ].join(' ').toLowerCase()
      return content.includes(query)
    })
  }, [memories, memorySearch])

  const handleUpload = () => {
    if (!selectedPdfFile) { setUploadError('Please choose a PDF file first.'); return }
    const shouldReplace =
      !!currentPath &&
      window.confirm(`A PDF already exists:\n${currentPath}\n\nReplace it with the new file?`)
    if (currentPath && !shouldReplace) return
    uploadMutation.mutate({ file: selectedPdfFile, replace: shouldReplace })
  }

  const moveExample = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= examplesDraft.length) return
    const next = [...examplesDraft]
    ;[next[index], next[target]] = [next[target], next[index]]
    setExamplesDraft(next.map((row, idx) => ({ ...row, order: idx + 1 })))
  }

  const addExample = () => {
    const nextIndex = examplesDraft.length + 1
    setExamplesDraft([
      ...examplesDraft,
      { id: `draft_${Date.now()}_${nextIndex}`, title: `New example ${nextIndex}`, text: '', enabled: true, order: nextIndex },
    ])
  }

  const formatMemoryDate = (value?: string | null) => {
    if (!value) return 'Unknown'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return 'Unknown'
    return date.toLocaleDateString()
  }

  const memoryDetail = (memory: AgentMemoryItem, key: string) => String(memory.content?.[key] || '').trim()

  const handleDeleteMemory = (memory: AgentMemoryItem) => {
    const confirmed = window.confirm(
      `Delete this long-term memory for ${memory.agent_slug}?\n\nThis removes it from future agent prompts.`
    )
    if (!confirmed) return
    deleteMemoryMutation.mutate(memory.id)
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page heading */}
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Knowledge Assets</h1>

      {/* Writing Examples */}
      <SectionCard
        title="Writing Examples for Final Proposal"
        subtitle="These examples teach the AI how to write in your organization's style and tone."
        testId="writing-examples-section"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        }
      >
        <div className="space-y-5">
          {/* Controls */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-44">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Example Bucket
              </label>
              <select
                value={bucket}
                onChange={(e) => setBucket(e.target.value as Bucket)}
                className="bp-select text-sm"
              >
                {BUCKET_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-400">{currentBucketMeta.help}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" onClick={addExample}>
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add Example
              </Button>
              <Button variant="secondary" size="sm" onClick={() => reseedMutation.mutate()} isLoading={reseedMutation.isPending}>
                Reset to Defaults
              </Button>
              <Button size="sm" onClick={() => saveExamplesMutation.mutate()} isLoading={saveExamplesMutation.isPending}>
                Save Examples
              </Button>
            </div>
          </div>

          {examplesSavedMessage && (
            <div className="bp-alert-success animate-slide-up">
              <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {examplesSavedMessage}
            </div>
          )}
          {examplesError && (
            <div className="bp-alert-danger animate-slide-up">
              <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {examplesError}
            </div>
          )}
          {promptExamplesQuery.isLoading && (
            <p className="text-sm text-slate-400 animate-pulse">Loading examples…</p>
          )}

          {/* Example rows */}
          <div className="space-y-3">
            {examplesDraft.map((row, idx) => (
              <div key={row.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                {/* Row controls */}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={row.title}
                    onChange={(e) => {
                      const next = [...examplesDraft]
                      next[idx] = { ...row, title: e.target.value }
                      setExamplesDraft(next)
                    }}
                    placeholder="Example title"
                    className="bp-input min-w-52 flex-1 text-sm"
                  />
                  {/* Toggle */}
                  <div className="flex items-center gap-2">
                    <Toggle
                      checked={row.enabled}
                      onChange={(v) => {
                        const next = [...examplesDraft]
                        next[idx] = { ...row, enabled: v }
                        setExamplesDraft(next)
                      }}
                    />
                    <span className={`text-xs font-medium ${row.enabled ? 'text-blue-700' : 'text-slate-400'}`}>
                      {row.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {/* Reorder */}
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => moveExample(idx, -1)}
                      disabled={idx === 0}
                      className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                      title="Move up"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveExample(idx, 1)}
                      disabled={idx === examplesDraft.length - 1}
                      className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                      title="Move down"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  {/* Delete */}
                  <button
                    type="button"
                    onClick={() => setExamplesDraft(examplesDraft.filter((item) => item.id !== row.id))}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                    title="Delete example"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                {/* Text area */}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Example Text</label>
                  <textarea
                    value={row.text}
                    onChange={(e) => {
                      const next = [...examplesDraft]
                      next[idx] = { ...row, text: e.target.value }
                      setExamplesDraft(next)
                    }}
                    className="bp-textarea h-48 w-full text-sm font-mono"
                    placeholder="Paste a completed proposal text example here…"
                  />
                  <p className="mt-1 text-right text-xs text-slate-400">{row.text.length.toLocaleString()} chars</p>
                </div>
              </div>
            ))}

            {examplesDraft.length === 0 && !promptExamplesQuery.isLoading && (
              <div className="flex flex-col items-center rounded-xl border-2 border-dashed border-slate-200 py-10 text-center">
                <p className="text-sm font-medium text-slate-500">No examples yet</p>
                <p className="mt-1 text-xs text-slate-400">Click "Add Example" or "Reset to Defaults" to get started.</p>
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* PDF Upload */}
      <SectionCard
        title="Project Experience PDF"
        subtitle="The AI reads this document to find relevant past projects when building proposals."
        testId="project-experience-pdf-section"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        }
      >
        <div className="space-y-4">
          {/* Current file */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Current File</p>
            {assetsQuery.isLoading ? (
              <div className="h-4 w-48 rounded bp-skeleton animate-pulse" />
            ) : (
              <p className={`break-all font-mono text-xs ${currentPath ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                {currentPath || 'No file configured yet'}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Use JYC project tool</p>
                <p className="mt-1 text-xs text-slate-500">
                  {projectToolModeEnabled
                    ? 'Step 4 uses the JYC MCP project catalog.'
                    : currentPath
                      ? 'Step 4 uses the uploaded Project Experience PDF instead of the JYC MCP project catalog.'
                      : 'No PDF is configured, so Step 4 falls back to the JYC MCP project catalog.'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Toggle
                  checked={projectToolModeEnabled}
                  ariaLabel="Use JYC project tool"
                  disabled={assetsQuery.isLoading || updateAssetsMutation.isPending}
                  onChange={(enabled) => {
                    setUploadError('')
                    updateAssetsMutation.mutate({ catalant_project_tool_mode_enabled: enabled })
                  }}
                />
                <span className={`text-xs font-semibold ${projectToolModeEnabled ? 'text-blue-700' : 'text-slate-500'}`}>
                  {projectToolModeEnabled ? 'Tool On' : 'Tool Off'}
                </span>
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <DropZone onFile={(f) => { setUploadError(''); setSelectedPdfFile(f) }} disabled={uploadMutation.isPending} />

          {selectedPdfFile && (
            <div className="flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium">{selectedPdfFile.name}</span>
                <span className="text-blue-500">({(selectedPdfFile.size / 1024).toFixed(0)} KB)</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelectedPdfFile(null)}>Remove</Button>
                <Button onClick={handleUpload} isLoading={uploadMutation.isPending} size="sm">
                  Upload PDF
                </Button>
              </div>
            </div>
          )}

          {uploadError && (
            <div className="bp-alert-danger animate-slide-up">
              <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {uploadError}
            </div>
          )}
        </div>
      </SectionCard>

      {/* Umbrex DC Bio */}
      <SectionCard
        title="Umbrex DC Intro & Bio"
        subtitle="Foundation text for the Relevant Experience section of every Umbrex proposal."
        testId="umbrex-dc-bio-section"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25a7.5 7.5 0 0115 0v.75h-15v-.75z" />
          </svg>
        }
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">DC in the JYC expert catalog</p>
                <p className="mt-1 text-xs text-slate-500">
                  The proposal agent uses this expert&apos;s summary as DC&apos;s bio. The text box below is the
                  fallback for when the catalog is unreachable or the expert has no summary.
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => expertCatalogQuery.refetch()}
                isLoading={expertCatalogQuery.isFetching}
              >
                Refresh
              </Button>
            </div>

            {expertCatalogQuery.isLoading ? (
              <div className="mt-3 h-4 w-48 rounded bp-skeleton animate-pulse" />
            ) : catalogOk ? (
              <div className="mt-3 space-y-3">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Matched expert
                  <select
                    value={savedDcExpertId}
                    aria-label="DC expert"
                    onChange={(e) => {
                      setDcBioError('')
                      saveDcExpertMutation.mutate(e.target.value)
                    }}
                    disabled={saveDcExpertMutation.isPending}
                    className="bp-input mt-1.5 w-full text-sm font-normal normal-case tracking-normal"
                  >
                    <option value="">Match by name ("DC")</option>
                    {experts.map((expert) => (
                      <option key={expert.expert_id} value={expert.expert_id}>
                        {expert.name}
                        {expert.expert_type ? ` (${expert.expert_type})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                {dcExpert ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <p className="text-sm font-semibold text-slate-900">{dcExpert.name}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                      {catalogBio || 'This expert has no summary in the catalog; the text below will be used.'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-amber-700">
                    No expert named &quot;DC&quot; in the catalog. Pick one above or rely on the text below.
                  </p>
                )}
                <p className="text-xs text-slate-500">{experts.length} expert(s) in the catalog.</p>
              </div>
            ) : (
              <p className="mt-3 text-sm text-amber-700">
                Expert catalog unavailable: {String(expertCatalogQuery.data?.message || expertCatalogQuery.error || 'unknown error')}
              </p>
            )}
          </div>

          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Fallback bio — currently using: {effectiveBioSource}
          </p>
          <textarea
            value={dcBioValue}
            onChange={(e) => {
              setDcBioSaved(false)
              setDcBioError('')
              setDcBioDraft(e.target.value)
            }}
            aria-label="Umbrex DC bio"
            placeholder="Paste DC's intro and bio. The Umbrex proposal agent builds Relevant Experience on this text."
            className="bp-input h-40 w-full text-sm"
            disabled={assetsQuery.isLoading || saveDcBioMutation.isPending}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              {dcBioValue.trim().length.toLocaleString()} characters
              {!savedDcBio.trim() && !dcBioDirty ? ' · not configured yet; proposals fall back to the JD break-apart only.' : ''}
            </p>
            <div className="flex items-center gap-2">
              {dcBioSaved && !dcBioDirty ? <span className="text-xs font-semibold text-green-700">Saved</span> : null}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDcBioDraft(null)}
                disabled={!dcBioDirty || saveDcBioMutation.isPending}
              >
                Discard
              </Button>
              <Button
                size="sm"
                onClick={() => saveDcBioMutation.mutate(dcBioValue)}
                isLoading={saveDcBioMutation.isPending}
                disabled={!dcBioDirty}
              >
                Save DC Bio
              </Button>
            </div>
          </div>
          {dcBioError && <div className="bp-alert-danger animate-slide-up">{dcBioError}</div>}

          <div className="pt-2" data-testid="learned-shorthand">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Learned DC shorthand</p>
            <p className="mt-1 text-xs text-slate-500">
              Learned automatically each time an Umbrex project pool is accepted: which code in DC notes meant which
              catalog project. The project selector searches for these projects when the code appears again.
            </p>
            {shorthandEntries.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">Nothing learned yet.</p>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
                {shorthandEntries.map((entry) => (
                  <li key={`${entry.code}::${entry.family_key}`} className="flex items-start gap-3 px-3 py-2 text-sm">
                    <span className="min-w-[4rem] font-mono font-semibold text-slate-900">{entry.code}</span>
                    <span className="flex-1 text-slate-700">
                      {entry.meaning}
                      <span className="block truncate text-xs text-slate-500" title={entry.search_text}>
                        {entry.search_text}
                      </span>
                    </span>
                    <span className="whitespace-nowrap text-xs text-slate-500">seen {entry.seen ?? 1}×</span>
                    <button
                      type="button"
                      className="text-xs font-semibold text-red-700 hover:underline"
                      aria-label={`Forget ${entry.code}`}
                      onClick={() => deleteShorthandMutation.mutate({ code: entry.code, family_key: entry.family_key })}
                      disabled={deleteShorthandMutation.isPending}
                    >
                      Forget
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {shorthandError && <div className="bp-alert-danger animate-slide-up mt-2">{shorthandError}</div>}
          </div>
        </div>
      </SectionCard>

      {/* Agent Long-Term Memory */}
      <SectionCard
        title="Agent Long-Term Memory"
        subtitle="Developer section for reflection memories injected into agent prompts."
        testId="agent-long-term-memory-section"
        icon={
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a3.75 3.75 0 013.75 3.75c0 1.18-.55 2.23-1.4 2.92-.64.52-1.1 1.2-1.1 2.08V16.5h-2.5v-1c0-.88-.46-1.56-1.1-2.08a3.75 3.75 0 012.35-6.67z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 19.5h4.5M12 3v1.5m6.36.64l-1.06 1.06M21 12h-1.5M4.5 12H3m3.7-5.8L5.64 5.14" />
          </svg>
        }
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Developer Section
              </span>
              <p className="mt-2 text-sm text-slate-500">
                Active memories: {agentMemoriesQuery.isLoading ? 'Loading' : memories.length.toLocaleString()}
              </p>
            </div>
            <div className="flex min-w-64 flex-1 justify-end gap-2">
              <input
                value={memorySearch}
                onChange={(e) => setMemorySearch(e.target.value)}
                className="bp-input max-w-sm text-sm"
                placeholder="Search memories, agents, preferences..."
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => agentMemoriesQuery.refetch()}
                isLoading={agentMemoriesQuery.isFetching}
              >
                Refresh
              </Button>
            </div>
          </div>

          {memoryDeleteError && (
            <div className="bp-alert-danger animate-slide-up">
              <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {memoryDeleteError}
            </div>
          )}

          <div className="max-h-[28rem] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50">
            {agentMemoriesQuery.isLoading ? (
              <div className="space-y-3 p-4">
                <div className="h-20 rounded-xl bp-skeleton animate-pulse" />
                <div className="h-20 rounded-xl bp-skeleton animate-pulse" />
              </div>
            ) : filteredMemories.length > 0 ? (
              <div className="divide-y divide-slate-200">
                {filteredMemories.map((memory) => (
                  <div key={memory.id} className="bg-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{memory.agent_slug}</span>
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            {memory.platform_type}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                            Step {memory.source_step_number || '?'}
                          </span>
                          {memory.source_rfp_id && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                              RFP {memory.source_rfp_id}
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-sm leading-6 text-slate-700">{memory.memory_text || 'No memory text stored.'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteMemory(memory)}
                        disabled={deleteMemoryMutation.isPending}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                        title="Delete memory"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    <div className="mt-3 grid gap-3 text-xs text-slate-500 md:grid-cols-3">
                      <div>
                        <p className="font-semibold uppercase tracking-wide text-slate-400">Applies When</p>
                        <p className="mt-1">{memoryDetail(memory, 'applies_when') || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="font-semibold uppercase tracking-wide text-slate-400">Preference</p>
                        <p className="mt-1">{memoryDetail(memory, 'preference') || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="font-semibold uppercase tracking-wide text-slate-400">Behavior</p>
                        <p className="mt-1">{memoryDetail(memory, 'agent_behavior') || 'Not specified'}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-slate-400">Updated {formatMemoryDate(memory.updated_at || memory.created_at)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center py-10 text-center">
                <p className="text-sm font-medium text-slate-500">
                  {memorySearch.trim() ? 'No memories match your search' : 'No active long-term memories yet'}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Memories are created after Step 5 acceptance when a step had human feedback.
                </p>
              </div>
            )}
          </div>
        </div>
      </SectionCard>

    </div>
  )
}
